/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Page Tariffs And Services Sagas
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.4.0
 */

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { EHttpMethod } from '@/main/modules/app/appConstants';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { IOptionItemProps } from '@components/common/Select/Select';
import {
    API_HOSTNAME,
    BASKET_COUNT,
    COUNTRIES,
    OPTIONS,
    PRODUCTS,
    PRODUCTS_GET_CATEGORY_LIST,
    PRODUCTS_GET_META,
    ROAMING,
    SUBSCRIBER_MOBILE_BASKET,
} from '@modules/app/Api';
import { appHistoryPush } from '@modules/app/appHistory';
import { LOAD_INTERNAL_TRANSLATIONS_SUCCESS } from '@modules/internationalization/constants';
import { getErrorTranslation } from '@modules/internationalization/helpers/intGetErrorTrnaslation';
import { makeSelectTranslationPageProducts } from '@modules/internationalization/intSelectors';
import { IPageProductsTranslation } from '@modules/internationalization/types/pages/products';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import {
    onboardingInitStartAction,
    onboardingPageReadyForStartAction,
} from '@modules/onboarding/onboardingActions';
import { ONBOARDING_INIT_SUCCESS } from '@modules/onboarding/onboardingConstants';
import { EOnboardingPage } from '@modules/onboarding/types/onboardingTypes';
import { ELocalStorageKeys } from '@modules/storage/storageTypes';
import {
    getItemFromLocalStorage,
    removeItemLocalStorage,
    setItemToLocalStorage,
} from '@modules/storage/storageUtils';
import {
    getSubscriberRegionsSaga,
    getSubscribersBasketRegionsSaga,
    getSubscribersRegionsBiIdsSaga,
} from '@modules/subscribers/subscribersRegionsSaga';
import { ESubscribersBasketRegionType } from '@modules/subscribers/subscribersTypes';
import { wizardStorageClearStepsAction } from '@modules/wizard/wizardActions';
import { EWizardName, WIZARD_STORAGE_CLEAR_STEPS_SUCCESS } from '@modules/wizard/wizardConstants';
import { RoutePath } from '@routes/constants/routesConstants';

import { requestSaga } from '../../sagas/requestSaga';
import { makeSelectFeatureEnabled } from '../PageAuth/selectors/authSelectors';
import { IAuthRegion } from '../PageAuth/types/authTypes';
import { getTariffsAndServicesListSaga } from './productsListSagas';
import {
    clearRoamingOptionsSearchAction,
    getTariffsAndServicesRegionListFailure,
    getTariffsAndServicesRegionListSuccess,
    productsClearAction,
    productsGetCategoryListAction,
    productsGetCategoryListFailure,
    productsGetCategoryListSuccess,
    productsGetMetaAction,
    productsGetMetaFailure,
    productsGetMetaSuccess,
    productsGetServiceListAction,
    productsSetCategorySelectedAction,
    productsSetFilterSelectionAction,
    productsSetPageFromAction,
    productsSetRegionSelected,
    productsSetTabSelected,
    roamingOptionsGetCountriesFailure,
    roamingOptionsGetCountriesSuccess,
    roamingOptionsGetFailure,
    roamingOptionsGetSuccess,
    setProductsItemsSelectedAction,
    setTariffsAndServicesFilterRegionVisible,
    setTariffsAndServicesPaymentSelected,
    setTariffsAndServicesRegionDisabledAction,
} from './tariffsAndServicesActions';
import {
    EProductsCategory,
    EProductsCategoryUrl,
    EProductsPageFrom,
    EProductsTab,
    GET_ROAMING_OPTIONS_COUNTRIES_SEARCH,
    GET_ROAMING_OPTIONS_FOR_COUNTRY,
    GET_TARIFFS_AND_SERVICES_REGION_LIST_ACTION,
    PRODUCTS_CLEAR_ACTION,
    PRODUCTS_CONNECT_SELECTED_SERVICES,
    PRODUCTS_GET_CATEGORY_LIST_ACTION,
    PRODUCTS_GET_CATEGORY_LIST_SUCCESS,
    PRODUCTS_GET_META_ACTION,
    PRODUCTS_GET_META_SUCCESS,
    PRODUCTS_GET_PRODUCT_LIST_SUCCESS,
    PRODUCTS_INIT_ACTION,
    PRODUCTS_SET_CATEGORY_SELECTED,
    PRODUCTS_SET_FILTER_SELECTION,
    PRODUCTS_SET_TAB_SELECTED,
    SET_TARIFFS_AND_SERVICES_ITEM_SELECTED,
    SET_TARIFFS_AND_SERVICES_PAYMENT_SELECTED,
    SET_TARIFFS_AND_SERVICES_REGION_SELECTED,
    SET_TARIFFS_AND_SERVICES_TARIFF_SELECTED,
    TARIFFS_AND_SERVICES_SET_SEARCH_VALUE,
} from './tariffsAndServicesConstants';
import { TProductItem } from './tariffsAndServicesProductTypes';
import {
    makeSelectIncludeCampaignServiceProducts,
    makeSelectIncludeCampaignTariffProducts,
    makeSelectPreselectedTariffsCategory,
    makeSelectProductsCategories,
    makeSelectProductsCategorySelected,
    makeSelectProductsFilterShowSelectedOnly,
    makeSelectProductsItemsSelectedAvailableIds,
    makeSelectProductsItemsSelectedConnectedIds,
    makeSelectProductsItemsSelectedIds,
    makeSelectProductsMeta,
    makeSelectProductsPageFrom,
    makeSelectProductsPaymentSelected,
    makeSelectProductsTabSelected,
    makeSelectTariffsAndServicesCategoryWithServicesList,
} from './tariffsAndServicesSelectors';
import {
    IInitTariffsAndServicesActionPayload,
    IProductsCategory,
    IProductsCategoryResponse,
    IProductsMeta,
    IProductsWithCategoryList,
    TInitTariffsAndServicesAction,
    TProductsConnectSelectedServicesAction,
    TRoamingOptionsAction,
    TRoamingOptionsCountriesAction,
    TTariffsAndServicesSetSearchValueAction,
} from './tariffsAndServicesTypes';

export function* initProductsSaga(action: TInitTariffsAndServicesAction): SagaIterator {

    yield put(productsClearAction());
    yield call(tariffsAndServicesLoadStorageSaga);
    yield put(productsSetPageFromAction({ pageFrom: action.payload.pageFrom, clientId: action.payload.clientId }));

    // очищаем информацию о роумиговых опция при выборе любой категории
    yield put(clearRoamingOptionsSearchAction());

    // получить мету
    yield put(productsGetMetaAction());
    yield take(PRODUCTS_GET_META_SUCCESS);


    // установить регион
    yield call(setTariffAndServicesRegionSelectSaga, action.payload);

    // получим дерево категорий
    yield put(productsGetCategoryListAction());
    yield take(PRODUCTS_GET_PRODUCT_LIST_SUCCESS);

    /**
     * Выбор категории
     */
    // after initialization select the service category
    const isSubscribersInRoamingNotificationEnabled: boolean = yield select(
        makeSelectFeatureEnabled(FeatureNameEnum.SUBSCRIBERS_IN_ROAMING_NOTIFICATION),
    );

    const productsMeta: IProductsMeta = yield select(makeSelectProductsMeta());
    const includeCampaignServiceProducts: boolean =
        yield select(makeSelectIncludeCampaignServiceProducts());
    const includeCampaignTariffProducts: boolean =
        yield select(makeSelectIncludeCampaignTariffProducts());

    let preselectedCategoryId: number | undefined;
    const preselectedCategory = localStorage.getItem(ELocalStorageKeys.PRESELECTED_PRODUCT_CATEGORY);
    // ищем в мете, есть ли там категория, для которой нужен преселект
    // и сохраняем в переменную значение id категории
    if (!!preselectedCategory && isSubscribersInRoamingNotificationEnabled) {
        Object.entries(productsMeta).forEach(([key, value]) => {
            if (key === preselectedCategory) {
                preselectedCategoryId = value;
            }
        });
    }

    const selectedCategoryHash = location.hash.replace('#', '');

    if (selectedCategoryHash === EProductsCategoryUrl.TARIFFS) {

        yield put(
            productsSetCategorySelectedAction(
                includeCampaignTariffProducts
                    ? productsMeta.campaignTariffsCategoryId
                    : includeCampaignServiceProducts
                    ? productsMeta.campaignServicesCategoryId
                    : productsMeta.tariffsCategoryId,
            ),
        );
    } else if (selectedCategoryHash === EProductsCategoryUrl.SERVICES) {

        // если есть преселект, то стандартная логика игнорируется
        if (!!preselectedCategory && !!preselectedCategoryId && isSubscribersInRoamingNotificationEnabled) {

            yield put(productsSetCategorySelectedAction(preselectedCategoryId));
            return;
        }

        yield put(
            productsSetCategorySelectedAction(
                includeCampaignServiceProducts
                    ? productsMeta.campaignServicesCategoryId
                    : includeCampaignTariffProducts
                    ? productsMeta.campaignTariffsCategoryId
                    : productsMeta.servicesCategoryId,
            ),
        );
    } else if (selectedCategoryHash === EProductsCategoryUrl.MEGAPOWERS && productsMeta.megaPowersCategoryId) {

        yield put(productsSetCategorySelectedAction(productsMeta.megaPowersCategoryId));
    } else {
        const categorySelected: number = yield select(makeSelectProductsCategorySelected());
        let defaultCategory = productsMeta.servicesCategoryId;
        if (includeCampaignTariffProducts) {
            defaultCategory = productsMeta.campaignTariffsCategoryId;
        } else if (includeCampaignServiceProducts) {
            defaultCategory = productsMeta.campaignServicesCategoryId;
        }

        yield put(productsSetCategorySelectedAction(categorySelected || defaultCategory));
    }
    // send business event
    yield put(
        logBusinessEvent({
            eventName: ELogBusinessEventName.USER_PAGE_PRODUCT,
            type: ELogBusinessEventType.common,
        }),
    );

    // Onboarding
    yield put(onboardingInitStartAction({ page: EOnboardingPage.products }));
    yield take(ONBOARDING_INIT_SUCCESS);

    yield put(onboardingPageReadyForStartAction());
}

export function* productsGetMetaSaga(): SagaIterator {
    try {
        /**
         * Признак, что можно выбрать только одну услугу, если в корзине больше одного абонента
         */
        let serviceMaxSelectIsSingle = false;

        /**
         * Показаны персональные предложения для выбранного вами абонента
         */
        let isCampaignInfoVisible = false;

        const pageFrom = yield select(makeSelectProductsPageFrom());

        if (pageFrom === EProductsPageFrom.SUBSCRIBERS) {
            const url = API_HOSTNAME + SUBSCRIBER_MOBILE_BASKET + BASKET_COUNT;
            const { count } = yield call(requestSaga, url, 'GET');
            serviceMaxSelectIsSingle = count > 1;
            isCampaignInfoVisible = count === 1;
        } else if (pageFrom === EProductsPageFrom.SUBSCRIBER_ID) {
            isCampaignInfoVisible = true;
        }

        /**
         * Meta
         */
        const data = yield call(requestSaga, `${API_HOSTNAME}${PRODUCTS_GET_META}`, EHttpMethod.GET);

        const parentCategoryId = EProductsCategory.ROOT;
        const popularCategoryId = data.popularCategoryId || EProductsCategory.POPULAR; // with default
        const servicesCategoryId = data.servicesCategoryId || EProductsCategory.SERVICES; // with default
        const tariffsCategoryId = data.tariffsCategoryId || EProductsCategory.TARIFFS; // with default
        const campaignServicesCategoryId = EProductsCategory.CAMPAIGN_SERVICES;
        const campaignTariffsCategoryId = EProductsCategory.CAMPAIGN_TARIFFS;
        const otherRatePlanCategoryId = data.otherRatePlanCategoryId || EProductsCategory.OTHER; // with default
        const megaPowersCategoryId = data.megaPowersCategoryId || EProductsCategory.MEGAPOWERS; // with default
        const roamingCategoryId = data.roamingCategoryId || EProductsCategory.ROAMING; // with default


        yield put(
            productsGetMetaSuccess({
                campaignServicesCategoryId,
                campaignTariffsCategoryId,
                isCampaignInfoVisible,
                parentCategoryId,
                popularCategoryId,
                serviceMaxSelectIsSingle,
                servicesCategoryId,
                servicesLimit: data.maxSelectedProductOfferings,
                tariffsCategoryId,
                activeCategoryTariffId: data.activeCategoryTariffId,
                otherRatePlanCategoryId,
                megaPowersCategoryId,
                roamingCategoryId,
            }),
        );
    } catch (e) {
        yield put(productsGetMetaFailure());
    }
}

export function* getTariffsAndServicesCategoryListSaga(): SagaIterator {
    try {
        const data: { productCategories: IProductsCategoryResponse[] } = yield call(
            requestSaga,
            `${API_HOSTNAME}${PRODUCTS_GET_CATEGORY_LIST}`,
            'GET',
        );

        const translation: IPageProductsTranslation = yield select(makeSelectTranslationPageProducts());
        const productsMeta: IProductsMeta = yield select(makeSelectProductsMeta());

        const productCategories: IProductsCategory[] = data.productCategories
            .map((category) => ({
                disabled: false,
                id: category.id,
                order: category.orderNum,
                parent: category.parentId,
                title: category.name,
            }))
            // Add category "Selected services" to products tree
            .concat([
                {
                    disabled: false,
                    id: productsMeta.servicesCategoryId,
                    order: -1,
                    parent: productsMeta.parentCategoryId,
                    title: translation.categoriesTitle.service,
                },
                {
                    disabled: false,
                    id: productsMeta.tariffsCategoryId,
                    order: -2,
                    parent: productsMeta.parentCategoryId,
                    title: translation.categoriesTitle.tariff,
                },
                {
                    disabled: (yield select(makeSelectProductsItemsSelectedIds())).length === 0,
                    id: EProductsCategory.SELECTED,
                    order: -3,
                    parent: productsMeta.servicesCategoryId,
                    title: translation.categoriesTitle.selected,
                },
                {
                    disabled: false,
                    id: productsMeta.campaignServicesCategoryId,
                    order: -4,
                    parent: productsMeta.parentCategoryId,
                    title: translation.categoriesTitle.campaignService,
                },
                {
                    disabled: false,
                    id: productsMeta.campaignTariffsCategoryId,
                    order: -5,
                    parent: productsMeta.parentCategoryId,
                    title: translation.categoriesTitle.campaignTariff,
                },
            ])
            .sort((a, b) => a.order - b.order);
        yield put(productsGetCategoryListSuccess(productCategories));
    } catch (e) {
        yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);
        const productsTranslation: IPageProductsTranslation = yield select(makeSelectTranslationPageProducts());
        const errorMessage = getErrorTranslation(e, productsTranslation.error);
        yield put(productsGetCategoryListFailure(errorMessage));
    }
}

export function* getTariffsAndServicesRegionListSaga(): SagaIterator {
    try {
        const response: IAuthRegion[] | undefined = yield call(getSubscriberRegionsSaga);
        if (response) {
            const data = response.map((item) => ({ label: item.name, value: item.id }));
            yield put(getTariffsAndServicesRegionListSuccess({ data }));
            return data;
        } else {
            yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);
            const { error }: IPageProductsTranslation = yield select(makeSelectTranslationPageProducts());
            yield put(getTariffsAndServicesRegionListFailure(error.codes['ui.region']));
        }
    } catch (e) {
        yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);
        const { error }: IPageProductsTranslation = yield select(makeSelectTranslationPageProducts());
        const errorMessage = getErrorTranslation(e, error);
        yield put(getTariffsAndServicesRegionListFailure(errorMessage));
    }
}

/**
 * Установить регион
 */
export function* setTariffAndServicesRegionSelectSaga(initData: IInitTariffsAndServicesActionPayload): SagaIterator {
    // список всех регионов пользователя
    const regionList: IOptionItemProps[] | undefined = yield call(getTariffsAndServicesRegionListSaga);

    if (!regionList) return;

    // скрыть выбор региона, если он один
    if (regionList.length > 1) {
        yield put(setTariffsAndServicesFilterRegionVisible({ filterRegionVisible: true }));
    }

    if (regionList.length <= 1 || initData.pageFrom === EProductsPageFrom.HEADER) {
        yield put(
            productsSetRegionSelected({
                regionSelected: regionList[0],
            }),
        );
    } else {
        // запрет менять регион, если не от шапки
        if (regionList.length > 1) {
            try {
                const region: IAuthRegion[] | undefined = initData.clientId
                    ? yield call(getSubscribersRegionsBiIdsSaga, [initData.clientId])
                    : yield call(getSubscribersBasketRegionsSaga, ESubscribersBasketRegionType.MOBILE);

                if (region?.[0]) {
                    yield put(
                        productsSetRegionSelected({
                            regionSelected: { label: region[0].name, value: region[0].id },
                        }),
                    );
                } else {
                    const { error }: IPageProductsTranslation = yield select(makeSelectTranslationPageProducts());
                    yield put(getTariffsAndServicesRegionListFailure(error.codes['ui.region']));
                }
            } catch (e) {
                // Doesn't affect on page logic - only ui info block
            }
        }
        yield put(setTariffsAndServicesRegionDisabledAction({ disabled: true }));
    }
}

/**
 * После выбора категории, если выбрана категория услуг, открыть следующую дочернюю категорию
 */
export function* getTariffsAndServicesCategorySelectedSaga(): SagaIterator {
    const categorySelected = yield select(makeSelectProductsCategorySelected());
    const productsMeta: IProductsMeta = yield select(makeSelectProductsMeta());

    if (!!categorySelected && categorySelected === productsMeta.servicesCategoryId) {
        const list: IProductsWithCategoryList = yield select(makeSelectTariffsAndServicesCategoryWithServicesList());
        const nextCategory = Object.values(list)
            .map((list) => ({
                ...list,
                productList: list.productList.filter((item: TProductItem) => item.personalOfferingId === undefined),
            }))
            .sort((a, b) => a.category.order - b.category.order)
            .find((cat) => cat.productList.length && cat.category.parent === productsMeta.servicesCategoryId);
        if (nextCategory) {

            yield put(productsSetCategorySelectedAction(nextCategory.category.id));
            return;
        }
    }

    if (!!categorySelected && categorySelected === productsMeta.tariffsCategoryId) {
        const preselectedCategoryId = yield select(makeSelectPreselectedTariffsCategory());
        if (preselectedCategoryId) {

            yield put(productsSetCategorySelectedAction(preselectedCategoryId));
        }
        return;
    }
}

export function* productSetCategorySelectedSaga(): SagaIterator {
    const categorySelected = yield select(makeSelectProductsCategorySelected());
    const tabSelected: EProductsTab = yield select(makeSelectProductsTabSelected());
    const productsMeta = yield select(makeSelectProductsMeta());
    const list: IProductsWithCategoryList = yield select(makeSelectTariffsAndServicesCategoryWithServicesList());
    if (
        (categorySelected === productsMeta.tariffsCategoryId && tabSelected === EProductsTab.CONNECTED) ||
        list[categorySelected]?.productList.length === 0
    ) {

        yield put(productsSetCategorySelectedAction(productsMeta.servicesCategoryId));
    }
}

export function* productsConnectSelectedServicesSaga(action: TProductsConnectSelectedServicesAction): SagaIterator {
    /**
     * Clear wizard data when click on connect/disconnect services button
     */
    yield put(
        wizardStorageClearStepsAction({
            wizardName: EWizardName.PRODUCT_OFFERINGS,
        }),
    );
    yield take(WIZARD_STORAGE_CLEAR_STEPS_SUCCESS);

    const pageFrom = yield select(makeSelectProductsPageFrom());

    if (pageFrom === EProductsPageFrom.SUBSCRIBER_ID && action.payload.clientId) {
        appHistoryPush(`${RoutePath.wizardProductOfferingsFromSubscribers}/${action.payload.clientId}`);
    } else if (pageFrom === EProductsPageFrom.SUBSCRIBERS) {
        appHistoryPush(RoutePath.wizardProductOfferingsFromSubscribers);
    } else if (pageFrom === EProductsPageFrom.SUBSCRIBER_ID_CHANGE_MEGA_POWERS) {
        appHistoryPush(`${RoutePath.wizardProductOfferingsFromSubscribers}/${action.payload.clientId}`);
    } else {
        appHistoryPush(RoutePath.wizardProductOfferings);
    }
}

export function* tariffsAndServicesSaveStorageSaga(): SagaIterator {
    const data: any = {};
    data.itemsSelectedAvailable = yield select(makeSelectProductsItemsSelectedAvailableIds());
    data.itemsSelectedConnected = yield select(makeSelectProductsItemsSelectedConnectedIds());
    data.paymentSelected = yield select(makeSelectProductsPaymentSelected());
    data.showSelectedOnly = yield select(makeSelectProductsFilterShowSelectedOnly());
    data.typeSelected = yield select(makeSelectProductsTabSelected());

    yield call(setItemToLocalStorage, ELocalStorageKeys.PRODUCTS, JSON.stringify(data));
}

export function* tariffsAndServicesLoadStorageSaga(): SagaIterator {
    const data = yield call(getItemFromLocalStorage, ELocalStorageKeys.PRODUCTS);
    if (data) {
        yield put(
            setProductsItemsSelectedAction({
                itemsSelectedAvailable: data.itemsSelectedAvailable || [],
                itemsSelectedConnected: data.itemsSelectedConnected || [],
            }),
        );
        yield put(setTariffsAndServicesPaymentSelected({ paymentSelected: data.paymentSelected }));
        yield put(productsSetTabSelected({ tabSelected: data.typeSelected }));
        yield put(productsSetFilterSelectionAction(data.showSelectedOnly || false));
    }
}

export function* tariffsAndServicesRegionSelectedSaga(): SagaIterator {
    yield put(
        setProductsItemsSelectedAction({
            itemsSelectedAvailable: [],
            itemsSelectedConnected: [],
        }),
    );
    yield call(productsSelectServiceProductSaga);
    yield put(productsGetServiceListAction());
    yield call(getTariffsAndServicesListSaga);
}

export function* tariffsAndServicesSetSearchValueSaga(action: TTariffsAndServicesSetSearchValueAction): SagaIterator {
    const categorySelected: number = yield select(makeSelectProductsCategorySelected());
    const productsMeta = yield select(makeSelectProductsMeta());
    if (action.payload === '' && categorySelected === EProductsCategory.SEARCH) {

        yield put(productsSetCategorySelectedAction(productsMeta.servicesCategoryId));
    }
}

/**
 * Trigger on product service click
 * On changing amount of chosen services, need to change the disabled property of the "Chosen services" menu item
 * For optimization reasons, the category list is updated only on when the amount changes from 0 -> 1 and vise versa
 */
export function* productsSelectServiceProductSaga(): SagaIterator {
    const productMeta: IProductsMeta = yield select(makeSelectProductsMeta());
    const categorySelected: number = yield select(makeSelectProductsCategorySelected());
    const productCategories: IProductsCategory[] = yield select(makeSelectProductsCategories());

    switch (categorySelected) {
        case productMeta.campaignServicesCategoryId:
        case productMeta.campaignTariffsCategoryId:
            yield put(
                productsGetCategoryListSuccess(
                    productCategories.map((category) => ({
                        ...category,
                        disabled: category.id === EProductsCategory.SELECTED,
                    })),
                ),
            );
            break;
        default:
            const selectedServiceProductsAmount = (yield select(makeSelectProductsItemsSelectedIds())).length;
            if (productCategories.length > 0 && selectedServiceProductsAmount <= 1) {
                yield put(
                    productsGetCategoryListSuccess(
                        productCategories.map((category) => ({
                            ...category,
                            disabled: category.id === EProductsCategory.SELECTED && selectedServiceProductsAmount === 0,
                        })),
                    ),
                );
            }
            break;
    }
}

export function* tariffsAndServicesProductsClearSaga(): SagaIterator {
    yield call(removeItemLocalStorage, ELocalStorageKeys.PRODUCTS);
}

export function* preselectedCategoryClearSaga(): SagaIterator {
    yield call(removeItemLocalStorage, ELocalStorageKeys.PRESELECTED_PRODUCT_CATEGORY);

    // очищаем информацию о роумиговых опция при выборе любой категории
    yield put(clearRoamingOptionsSearchAction());
}

export function* getRoamingOptionsCountries(action: TRoamingOptionsCountriesAction): SagaIterator {
    try {
        const url = `${API_HOSTNAME}${PRODUCTS}${ROAMING}${COUNTRIES}?query=${action.payload.searchString}`;
        const data = yield call(requestSaga, url, EHttpMethod.GET);

        yield put(roamingOptionsGetCountriesSuccess({
            countries: data.countries,
        }));
    } catch (e) {
        const error = e?.toString() || 'unknown error';
        yield put(roamingOptionsGetCountriesFailure(error));
    }
}

export function* getRoamingOptions(action: TRoamingOptionsAction): SagaIterator {
    try {
        const url =
            `${API_HOSTNAME}${PRODUCTS}${ROAMING}${OPTIONS}?countryCode=${action.payload.selectedCountryCode}&regionId=${action.payload.regionId}`;
        const data = yield call(requestSaga, url, EHttpMethod.GET);

        yield put(roamingOptionsGetSuccess({
            roamingOptions: data.options,
        }))
    } catch (e) {
        const error = e?.toString() || 'unknown error';
        yield put(roamingOptionsGetFailure(error));
    }
}

export function* productsSagas(): SagaIterator {
    yield all([
        takeEvery(PRODUCTS_INIT_ACTION, initProductsSaga),
        takeEvery(PRODUCTS_GET_META_ACTION, productsGetMetaSaga),
        takeEvery(PRODUCTS_GET_CATEGORY_LIST_ACTION, getTariffsAndServicesCategoryListSaga),
        takeEvery(
            [
                PRODUCTS_SET_TAB_SELECTED,
                PRODUCTS_SET_CATEGORY_SELECTED,
                PRODUCTS_GET_CATEGORY_LIST_SUCCESS,
                PRODUCTS_GET_PRODUCT_LIST_SUCCESS,
            ],
            getTariffsAndServicesCategorySelectedSaga,
        ),
        takeEvery(GET_TARIFFS_AND_SERVICES_REGION_LIST_ACTION, getTariffsAndServicesRegionListSaga),
        takeEvery(PRODUCTS_CONNECT_SELECTED_SERVICES, productsConnectSelectedServicesSaga),
        takeEvery(
            [
                PRODUCTS_SET_FILTER_SELECTION,
                PRODUCTS_SET_TAB_SELECTED,
                SET_TARIFFS_AND_SERVICES_ITEM_SELECTED,
                SET_TARIFFS_AND_SERVICES_PAYMENT_SELECTED,
                SET_TARIFFS_AND_SERVICES_REGION_SELECTED,
            ],
            tariffsAndServicesSaveStorageSaga,
        ),
        takeEvery(SET_TARIFFS_AND_SERVICES_REGION_SELECTED, tariffsAndServicesRegionSelectedSaga),
        takeEvery([PRODUCTS_SET_TAB_SELECTED, PRODUCTS_GET_PRODUCT_LIST_SUCCESS], productSetCategorySelectedSaga),
        takeEvery(TARIFFS_AND_SERVICES_SET_SEARCH_VALUE, tariffsAndServicesSetSearchValueSaga),
        takeEvery(
            [
                PRODUCTS_SET_TAB_SELECTED,
                SET_TARIFFS_AND_SERVICES_ITEM_SELECTED,
                SET_TARIFFS_AND_SERVICES_TARIFF_SELECTED,
            ],
            productsSelectServiceProductSaga,
        ),
        takeEvery(PRODUCTS_CLEAR_ACTION, tariffsAndServicesProductsClearSaga),
        takeEvery(GET_ROAMING_OPTIONS_COUNTRIES_SEARCH, getRoamingOptionsCountries),
        takeEvery(GET_ROAMING_OPTIONS_FOR_COUNTRY, getRoamingOptions),

        // очищаем преселект на последнем вызове данного экшена
        takeLatest(PRODUCTS_SET_CATEGORY_SELECTED, preselectedCategoryClearSaga),
    ]);
}
